export const A2HS_DISMISSED_KEY = 'a2hs-hint-dismissed';

/**
 * True only when we should nudge the user to install: iOS Safari, not already
 * running as an installed standalone app, and not previously dismissed.
 */
export function shouldShowA2hsHint(win: Window): boolean {
  const nav = win.navigator as Navigator & { standalone?: boolean };
  const ua = nav.userAgent || '';

  // Already installed (standalone)? Never nudge.
  const standalone =
    nav.standalone === true ||
    (typeof win.matchMedia === 'function' && win.matchMedia('(display-mode: standalone)').matches);
  if (standalone) return false;

  // iOS device? iPhone/iPad/iPod, plus iPadOS which masquerades as macOS.
  const isIosClassic = /iphone|ipad|ipod/i.test(ua);
  const isIpadOs = /macintosh/i.test(ua) && (nav.maxTouchPoints ?? 0) > 1;
  if (!isIosClassic && !isIpadOs) return false;

  // Show on iOS browsers that support "Add to Home Screen": Safari, Chrome (CriOS),
  // and Edge (EdgiOS) — Chrome/Edge route through the iOS share sheet just like Safari.
  // Reject browsers/webviews that cannot Add to Home Screen: Firefox (FxiOS), Opera
  // (OPiOS/OPT), the Google app (GSA), and common in-app webviews (Facebook, Instagram).
  // Their UA also contains "Safari", so these tokens MUST be checked before the
  // Safari allow-check below.
  if (/fxios|opios|opt\/|gsa|fban|fbav|instagram/i.test(ua)) return false;
  if (!/safari/i.test(ua)) return false;

  // Previously dismissed? Best-effort; storage failure means "not dismissed".
  let dismissed = false;
  try {
    dismissed = win.localStorage.getItem(A2HS_DISMISSED_KEY) === 'true';
  } catch {
    dismissed = false;
  }
  return !dismissed;
}

/** Persist a permanent dismissal. No-op if storage is unavailable. */
export function dismissA2hsHint(win: Window): void {
  try {
    win.localStorage.setItem(A2HS_DISMISSED_KEY, 'true');
  } catch {
    // Storage unavailable (private mode, etc.) — banner just won't stay dismissed.
  }
}
